import type { GoogleRecheckResult } from '@/types';
import { formatIstRefundTime } from './storePrice';

/** Google's purchaseState: 0 purchased, 1 cancelled, 2 pending. */
const GOOGLE_STATES: Record<number, string> = {
  0: 'Purchased',
  1: 'Cancelled',
  2: 'Pending',
};

/** Google's voidedReason and voidedSource (Voided Purchases API). */
const VOIDED_REASONS: Record<number, string> = {
  0: 'other',
  1: 'remorse',
  2: 'not received',
  3: 'defective',
  4: 'accidental purchase',
  5: 'fraud',
  6: 'friendly fraud',
  7: 'chargeback',
};
const VOIDED_SOURCES: Record<number, string> = {
  0: 'by the buyer',
  1: 'by the developer',
  2: 'by Google',
};

/**
 * A failed Google Play purchase with a token whose Google check failed (or an
 * old row with no stored answer) can be checked again. Rows that may have been
 * granted (a check in flight or never finished, kept for review, completed, or
 * status set by hand) are refused by the server; the button is hidden for them.
 * The server also refuses an old row that took over 2 minutes to fail.
 */
export function canRecheckGoogle(purchase: {
  platform?: string;
  status?: string;
  purchaseToken?: string;
  rawResponse?: Record<string, unknown> | null;
}): boolean {
  const raw = purchase.rawResponse;
  return (
    purchase.platform === 'google_play' &&
    purchase.status === 'failed' &&
    !!purchase.purchaseToken &&
    (raw == null || (raw.type === 'google_verify_failed' && raw.adminStatusChange == null))
  );
}

function itemsText(items: Record<string, unknown> | undefined): string {
  const parts = Object.entries(items ?? {})
    .filter(([, v]) => typeof v === 'number' || typeof v === 'string')
    .map(([k, v]) => `${k} ${v}`);
  return parts.length ? parts.join(', ') : 'the product';
}

/** The toast after "Check again with Google". */
export function describeGoogleRecheck(result: GoogleRecheckResult): { ok: boolean; text: string } {
  if (result.credited) {
    const order = result.orderId ? ` (${result.orderId})` : '';
    return {
      ok: true,
      text: `Google says purchased${order}: granted ${itemsText(result.itemsGranted)}`,
    };
  }
  if (result.status === 'cancelled') {
    return {
      ok: false,
      text: `Order ${result.orderId ?? ''} was already granted by an admin: row closed, nothing granted again`,
    };
  }
  if (result.purchaseState === 0) {
    // Purchased, but held back (support may have granted it, or the order
    // could not be recorded): the server's sentence says which.
    return { ok: false, text: result.error ?? 'Google says purchased, but nothing was granted' };
  }
  if (result.purchaseState === 2) {
    return { ok: false, text: 'Google still says pending: nothing granted' };
  }
  if (result.purchaseState === 1) {
    return { ok: false, text: 'Google says cancelled: nothing granted' };
  }
  return {
    ok: false,
    text: `Google check failed (${result.error ?? 'no answer'}): nothing granted`,
  };
}

/**
 * One line for the answer the backend keeps on a failed Google row
 * (`rawResponse.type` google_verify_failed; google_paid_needs_review when
 * Google said purchased but support may already have granted it;
 * google_voided when Google's voided list has it), or null.
 */
export function googleCheckSummary(raw: Record<string, unknown> | undefined): string | null {
  const last = (r: Record<string, unknown>) =>
    typeof r.lastCheckedAt === 'string' ? formatIstRefundTime(r.lastCheckedAt) : '—';
  if (raw?.type === 'google_paid_needs_review') {
    const grant =
      typeof raw.possibleAdminGrantPurchaseId === 'string' ? raw.possibleAdminGrantPurchaseId : '?';
    return `Purchased · not granted: support may already have granted it (purchase ${grant}) · last ${last(raw)}`;
  }
  if (raw?.type === 'google_voided') {
    const when = typeof raw.voidedAt === 'string' ? ` ${formatIstRefundTime(raw.voidedAt)}` : '';
    const why = [
      typeof raw.voidedReason === 'number' ? VOIDED_REASONS[raw.voidedReason] : undefined,
      typeof raw.voidedSource === 'number' ? VOIDED_SOURCES[raw.voidedSource] : undefined,
    ].filter(Boolean);
    const detail = why.length ? ` (${why.join(', ')})` : '';
    return `Voided by Google${when}${detail} · not granted · last ${last(raw)}`;
  }
  if (!raw || raw.type !== 'google_verify_failed') return null;
  const state =
    raw.purchaseState === 0 && typeof raw.error === 'string'
      ? `Purchased · not granted: ${raw.error}`
      : typeof raw.purchaseState === 'number'
        ? (GOOGLE_STATES[raw.purchaseState] ?? `State ${raw.purchaseState}`)
        : typeof raw.error === 'string'
          ? raw.error
          : 'No answer';
  const n = typeof raw.failedAttempts === 'number' ? raw.failedAttempts : 1;
  const checks = `${n} ${n === 1 ? 'check' : 'checks'}`;
  return `${state} · ${checks} · last ${last(raw)}`;
}
